import { yupResolver } from '@hookform/resolvers/yup';
import {
  Alert,
  Backdrop,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { FormMode, Marketplaces } from 'common/constants/enums';
import { type FC, useEffect, useRef, useState } from 'react';
import { FormProvider, FormState, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { pageBuilderV2ValidationSchema } from '../../../../common/validationSchemas';
import { pagesApiSlice } from '../../../../store/api/pages-api/pages-api';
import { PageBuilderV2Header } from './components/page-builder-v2-header';
import { PageBuilderV2Loader } from './components/page-builder-v2-loader';
import {
  BannersSection,
  BuyingGuideSection,
  MetadataSection,
  ProductsSection,
  PublisherImagesSection,
} from './components/sections';
import { styles } from './page-builder-v2.styles';
import type { CommonPageForm } from './page-builder-v2.types';
import { buildPageUrl } from './utils/build-page-url';
import { v2Theme } from './v2-theme';

interface IPageBuilderV2Props {
  isV2Enabled: boolean;
  pageId?: number;
  formMode?: FormMode;
}

const usePagesForm = (formMode: FormMode = FormMode.CREATE) => {
  const [searchParams] = useSearchParams();

  const params = useParams();
  const marketplaceParam = searchParams.get('marketplace') as Marketplaces;
  const domainParam = searchParams.get('domain');
  const pageId = params.id;

  const queryResult = pagesApiSlice.useGetFullPageDataQuery({
      brand: params.brand,
      domain: domainParam,
      formMode,
      id: Number(pageId),
      marketplace: marketplaceParam,
      publisherName: params.publisher,
      slug: params.slug,
    },
    {
      skip: formMode === FormMode.CREATE || !pageId,
    });

  const {
    data,
    isFetching,
  } = queryResult;

  const methods = useForm<CommonPageForm>({ resolver: yupResolver(pageBuilderV2ValidationSchema) });

  useEffect(() => {
    if (data?.page) {
      methods.reset(data.page, { keepDirty: false });
    }
  }, [data]);

  return ({
    isFetching,
    methods,
  });
};

/**
 * Recursively checks whether the object contains an actual value of `true`.
 */
function isTrulyDirty(val: any): boolean {
  if (val === true) return true;
  if (typeof val === 'object' && val !== null) {
    return Object.values(val).some(isTrulyDirty);
  }
  return false;
}

/**
 * useFieldArray injects an internal `id` field into every array item on registration,
 * making RHF think the form is dirty even without user edits. This function considers
 * the form dirty only if there is a scalar field change OR an array item has at least
 * one dirty field that isn't just the phantom `id`.
 */
function useIsFormDirty(
  dirtyFields: Record<string, any>,
  currentValues: CommonPageForm,
  defaultValues: CommonPageForm | undefined,
): boolean {
  const arrayKeys = ['products', 'buyingGuide', 'publisherImages', 'pageBanners'];

  const scalarKeys = Object.keys(dirtyFields).filter((k) => {
    if (arrayKeys.includes(k)) return false;
    if (!isTrulyDirty(dirtyFields[k])) return false;

    const cVal = currentValues[k as keyof CommonPageForm];
    const dVal = defaultValues?.[k as keyof CommonPageForm];

    const normC = cVal === null || cVal === undefined ? '' : cVal;
    const normD = dVal === null || dVal === undefined ? '' : dVal;

    if (typeof normC === 'object' || typeof normD === 'object') {
      return JSON.stringify(normC) !== JSON.stringify(normD);
    }

    return normC !== normD;
  });

  if (scalarKeys.length > 0) return true;

  return arrayKeys.some((key) => {
    if (key === 'products') {
      const currentProducts = currentValues?.products || [];
      const defaultProducts = defaultValues?.products || [];

      const visibleCurrent = currentProducts.filter((p) => p.status !== 'excluded');
      const visibleDefault = defaultProducts.filter((p) => p.status !== 'excluded');

      if (visibleCurrent.length !== visibleDefault.length) return true;

      const hasOrderChanged = visibleCurrent.some(
        (p, i) => p.original_id !== visibleDefault[i]?.original_id,
      );
      if (hasOrderChanged) return true;

      for (let i = 0; i < visibleCurrent.length; i++) {
        const c = visibleCurrent[i];
        const d = visibleDefault[i];

        if (c.value !== d.value) return true;
        if (c.title !== d.title) return true;
        if (c.order !== d.order) return true;
        if (c.linked_product_id !== d.linked_product_id) return true;
      }

      const dfProducts = dirtyFields.products;
      if (Array.isArray(dfProducts)) {
        return currentProducts.some((cP, idx) => {
          if (cP.status === 'excluded') return false;
          const dP = dfProducts[idx];
          if (!dP) return false;

          return Object.keys(dP).some((k) => {
            if (['id', 'status', 'value', 'title', 'order', 'linked_product_id'].includes(k)) {
              return false;
            }
            return isTrulyDirty(dP[k]);
          });
        });
      }
      return false;
    }

    const arr = dirtyFields[key] as Record<string, any>[] | undefined;
    return arr?.some((item) => {
      if (!item) return false;
      return Object.keys(item).some((k) => k !== 'id' && isTrulyDirty(item[k]));
    }) ?? false;
  });
}

export const PageBuilderV2: FC<IPageBuilderV2Props> = ({
  isV2Enabled,
  formMode = FormMode.CREATE,
}) => {
  const navigate = useNavigate();
  const [isSaving, setIsSaving] = useState(false);
  const productsReorderedRef = useRef(false);

  const [submitPageProcess] = pagesApiSlice.useSubmitPageProcessMutation();

  const {
    isFetching,
    methods,
  } = usePagesForm(formMode);

  const isActuallyDirty = useIsFormDirty(
    methods.formState.dirtyFields as Record<string, any>,
    methods.getValues(),
    methods.formState.defaultValues as CommonPageForm
  );

  const handleCancel = () => navigate('/pages');

  const onSubmit = async (formData: CommonPageForm) => {
    setIsSaving(true);

    try {
      const result = await submitPageProcess({
        formMode,
        formData,
        formState: {
          dirtyFields: methods.formState.dirtyFields,
        } as unknown as FormState<CommonPageForm>,
        productsReordered: productsReorderedRef.current,
      }).unwrap();
      productsReorderedRef.current = false;

      const pageUrl = buildPageUrl({
        brandName: formData.brand?.name,
        domain: formData.publisher?.domain,
        reverseProxyPath: formData.publisher?.reverse_proxy_path,
        slug: formData.slug,
      });

      navigate(`/pages/v2/success/${result.pageId}`, { replace: true, state: { pageUrl } });
    } catch (err: any) {
      setIsSaving(false);

      const errorMessage =
        err?.data?.errors?.[0]?.message || err?.message || 'Failed to save page. Please try again.';

      toast.error(errorMessage);
    }
  };

  if (!isV2Enabled) return null;

  if (isFetching) {
    return (
      <ThemeProvider theme={v2Theme}>
        <PageBuilderV2Loader />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={v2Theme}>
    <FormProvider {...methods}>
      <PageBuilderV2Header formMode={formMode} />
      <Backdrop
        open={isSaving}
        sx={{
          color: '#fff',
          flexDirection: 'column',
          gap: 2,
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
      >
        <CircularProgress color="inherit" size={48} />
        <Typography color="inherit" variant="body1">Saving page...</Typography>
      </Backdrop>

      <Box component="form" onSubmit={methods.handleSubmit(onSubmit)} sx={styles.formContainer}>
        <Stack spacing={5}>
          <MetadataSection formMode={formMode} />
          <BannersSection />
          <PublisherImagesSection />
          <BuyingGuideSection />
          <ProductsSection onProductsReordered={() => { productsReorderedRef.current = true; }} />

          {methods.formState.isSubmitted && (() => {
            const { errors } = methods.formState;
            const messages: string[] = [];

            if (errors.title?.message) messages.push(`Page Title: ${errors.title.message}`);
            if (errors.publisher?.message) messages.push(`Publisher: ${errors.publisher.message}`);
            if ((errors.products as any)?.message) messages.push(`Products: ${(errors.products as any).message}`);
            (errors.products as any[])?.forEach?.((productError, idx) => {
              productError?.content?.pros?.forEach?.((prosError: any, prosIdx: number) => {
                const msg = prosError?.value?.message || prosError?.message;
                if (msg) messages.push(`Product ${idx + 1} - Pros #${prosIdx + 1}: ${msg}`);
              });
              productError?.content?.faq?.forEach?.((faqError: any, faqIdx: number) => {
                if (faqError?.question?.message) {
                  messages.push(`Product ${idx + 1} - FAQ ${faqIdx + 1} Question: ${faqError.question.message}`);
                }
                if (faqError?.answer?.message) {
                  messages.push(`Product ${idx + 1} - FAQ ${faqIdx + 1} Answer: ${faqError.answer.message}`);
                }
              });
            });

            return messages.length > 0 ? (
              <Stack spacing={1}>
                {messages.map((msg) => (
                  <Alert key={msg} severity="error">{msg}</Alert>
                ))}
              </Stack>
            ) : null;
          })()}
        </Stack>

        <Box sx={styles.stickyBar}>
          <Box>
            {isActuallyDirty && (
              <Chip color="warning" label="Unsaved changes" size="small" variant="outlined" />
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Button onClick={handleCancel} size="medium" variant="outlined">
              Cancel
            </Button>
            <Button
              color="primary"
              disabled={isFetching || isSaving || !isActuallyDirty}
              size="medium"
              type="submit"
              variant="contained"
            >
              Save Page
            </Button>
          </Box>
        </Box>
      </Box>
    </FormProvider>
    </ThemeProvider>
  );
};
